<template>
  <v-card variant="flat" class="status-bar rounded-pill gm-card" :loading="appStore.stationBaseDataLoading">
    <div class="station-info">
      <div class="d-flex align-center justify-center rounded-lg overflow-hidden mr-4"
        style="width: 40px; height: 40px;">
        <img :src="stationBaseData?.station_picture || defaultStationImg" alt="" width="40" height="40"
          class="object-cover">
      </div>
      <div>
        <div>
          <span class="station-name">{{ stationBaseData?.name }}</span>
          <v-icon v-if="!isApprovePage" @click="openSidebar()" color="var(--color-primary)" v-ripple
            class="switch-icon rounded" size="24">
            <ArrowDownUp />
          </v-icon>
        </div>
        <div class="station-meta">
          <span class="meta-item">{{ stationBaseData?.province }}-{{ stationBaseData?.city }}</span>
          <v-divider vertical />
          <span class="meta-item">{{ stationBaseData?.weather?.day_air_temperature }}℃
            {{ stationBaseData?.weather?.day_weather }}</span>
          <v-divider vertical />
          <span class="meta-item">装机容量:
            <b>{{ fmt(stationBaseData?.max_power) }}MW</b>/<b>{{ fmt(stationBaseData?.capacity) }}MWh</b></span>
        </div>
      </div>
    </div>
    <div class="status-wrapper">
      <button class="status-scroll-btn" :disabled="!canScrollLeft" aria-label="向左滚动状态项" @click="scrollStatus(-1)">
        ‹
      </button>
      <div ref="statusScrollRef" class="status-scroll" @scroll="updateStatusScroll">
        <div class="status-list">
          <StatusItem :text="stationStatusMap.getText(realData?.stationStatus, '--')" v-tooltip="'当前运行状态'">
            <template #icon>
              <div class="charging-icon" :class="stationStatusMap.getClass(realData?.stationStatus)"></div>
            </template>
          </StatusItem>
          <StatusItem :text="mqttStatusMap.getText(realData?.mqttStatus, '--')" v-tooltip="'通信状态'">
            <template #icon>
              <WifiOff :class="mqttStatusMap.getClass(realData?.mqttStatus)" v-if="realData?.mqttStatus == 0" />
              <Wifi :class="mqttStatusMap.getClass(realData?.mqttStatus)" v-if="realData?.mqttStatus == 1" />
            </template>
          </StatusItem>
          <StatusItem :text="realData?.alarmCount > 0 ? realData?.alarmCount + '条' : '无告警'" v-tooltip="'告警数量'">
            <template #icon>
              <Siren :class="realData?.alarmCount > 0 ? 'text-red-darken-1' : 'text-green-darken-1'" />
            </template>
          </StatusItem>
          <StatusItem :text="(realData?.power ?? '0') + ' kW'" v-tooltip="'当前功率'">
            <template #icon>
              <img src="./img/ic_current.png" alt="">
            </template>
          </StatusItem>
          <StatusItem :text="(realData?.soc ?? '0') + ' %'" v-tooltip="'当前SOC'">
            <template #icon>
              <Battery :soc="realData?.soc" :showText="false" />
              <!-- 充电中时显示闪电图标 -->
              <img class="ml-4" src="./img/ic_flash.png" alt="" v-if="realData?.stationStatus == 3">
            </template>
          </StatusItem>
          <StatusItem :text="ot('eLinkOnline', 'eLinkTotal')" v-tooltip="'eLink在线数量/总数'">
            <template #icon>
              <!-- <Link2 /> -->
              <img src="./img/ic_elk.png" alt="" srcset="">
            </template>
          </StatusItem>
          <StatusItem :text="ot('eBlockCount', 'eBlockTotal')" v-tooltip="'eBlock在线数量/总数'">
            <template #icon>
              <!-- <Box /> -->
              <img src="./img/ic_eb.png" alt="" srcset="">
            </template>
          </StatusItem>
          <StatusItem :text="ot('meterOnline', 'meterTotal')" v-tooltip="'电表在线数量/总数'">
            <template #icon>
              <Gauge color="var(--color-primary)" />
            </template>
          </StatusItem>
        </div>
      </div>
      <button class="status-scroll-btn" :disabled="!canScrollRight" aria-label="向右滚动状态项" @click="scrollStatus(1)">
        ›
      </button>
    </div>
  </v-card>
</template>


<script setup>
import { inject, ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { ArrowDownUp } from 'lucide-vue-next'
import { useRoute } from 'vue-router'
import StatusItem from './StatusItem.vue'
import Battery from '@/components/Battery/index.vue'
import { CircleCheck, Wifi, WifiOff, ShieldCheck, Activity, Box, Link2, Gauge, Siren } from 'lucide-vue-next'
import { ws_req } from '@/ws/wss-request'
import { createStatusMap } from '@/utils'
import { watch } from 'vue'
import defaultStationImg from '@/assets/images/defaultStation.png'
import useAppStore from '@/store/modules/app'
import { useRealData } from '@/hooks/useRealData'

const route = useRoute()
const appStore = useAppStore()
// 兜底到 store，避免守卫回填落空时（如账号无电站）读到 undefined
const stationId = computed(() => {
  const id = route.query.stationId || appStore.stationId
  return id != null && id !== '' ? String(id) : ''
})
const isApprovePage = computed(() => route.path.startsWith('/approve'))
const statusScrollRef = ref(null)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)

function updateStatusScroll() {
  const el = statusScrollRef.value
  if (!el) return
  canScrollLeft.value = el.scrollLeft > 0
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
}

function scrollStatus(direction) {
  const el = statusScrollRef.value
  if (!el) return
  el.scrollBy({ left: direction * Math.max(el.clientWidth * 0.8, 120), behavior: 'smooth' })
}

onMounted(() => {
  nextTick(updateStatusScroll)
  window.addEventListener('resize', updateStatusScroll)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateStatusScroll)
})


const openStationSwitch = inject("openStationSwitch", (tab) => { })
const openSidebar = () => {
  if (!stationId.value) return
  const tabKey = `stationTab`;
  const tab = sessionStorage.getItem(tabKey)
  openStationSwitch(tab)
}



const stationBaseData = computed(() => appStore.stationBaseData)

const { realData, updateOnce } = useRealData({
  updateFunction: () => {
    return ws_req.request('station_realtime_data', {
      stationId: stationId.value
    })
  },
})


function fmt(value) {
  return value ? (value / 1000).toFixed(3) : '--'
}

function ot(onlineKey, totalKey) {
  return (realData.value[onlineKey] ?? '0') + '/' + (realData.value[totalKey] ?? '0')
}

// 守卫：stationId 为空时不发请求，避免 baseData/{undefined} 这类无效往返
if (stationId.value) {
  appStore.getBaseData(stationId.value)
}

watch(() => stationId.value, (newVal) => {
  if (!newVal) return
  appStore.getBaseData(newVal)
  updateOnce()
})


// 1:停机 2:待机 3:充电 4:放电
const stationStatusMap = createStatusMap({
  1: { text: '停机', class: 'stopped' },
  2: { text: '待机', class: 'standby' },
  3: { text: '充电', class: 'charging' },
  4: { text: '放电', class: 'discharge' },
})

const mqttStatusMap = createStatusMap({
  0: { text: '离线', class: 'text-gray-darken-1' },
  1: { text: '在线', class: 'text-green-darken-1' },
})



</script>

<style scoped lang="scss">
.status-bar {
  display: flex;
  padding: 6px 24px;
  align-items: center;
  flex-wrap: nowrap;
  overflow: hidden;

  .station-name {
    color: #2e2e2e;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 300px;
    font-family: HarmonyOS Sans SC, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: normal;
    overflow: hidden;
  }

  .station-info {
    display: flex;
    height: 100%;
    align-items: center;
    flex-shrink: 0;
  }

  .switch-icon {
    margin-left: 8px;
    padding: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease-in-out;

    &:hover {
      background-color: #C9E3FF;
    }
  }

  .station-meta {
    color: #2e2e2eb3;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    display: flex;

    :deep(.data-card__title) {
      font-size: 12px;
    }
  }


  .status-wrapper {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-left: 92px;
    flex: 1 1 auto;
    min-width: 0;
    justify-content: flex-end;

    .status-scroll {
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;
      flex: 1 1 auto;
      min-width: 0;
      max-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .status-list {
      display: flex;
      gap: 24px;
      align-items: center;
      flex-wrap: nowrap;
      width: max-content;
    }

    :deep(.status-item) {
      flex-shrink: 0;
      white-space: nowrap;
    }

    .status-scroll-btn {
      display: none;
      width: 20px;
      height: 20px;
      border: 0;
      border-radius: 50%;
      color: var(--color-primary);
      background: #eaf4ff;
      line-height: 18px;
      cursor: pointer;
      flex-shrink: 0;

      &:disabled {
        opacity: 0.35;
        cursor: default;
      }
    }


    .charging-icon {
      width: 8px;
      height: 8px;
      background: var(--color-charging);
      border-radius: 50%;
    }

    .charging-icon.charging {
      background: var(--color-charging);
    }

    .charging-icon.discharge {
      background: var(--color-discharge);
    }

    .charging-icon.stopped {
      background: var(--color-stopped);
    }

    .charging-icon.standby {
      background: var(--color-standby);
    }

  }

}

// 小屏适配：缩小间距、缩短左外边距，确保最右侧内容不被截断
@media screen and (max-width: 1600px) {
  .status-bar {
    .status-wrapper {
      margin-left: 32px;
      gap: 16px;
    }
  }
}

@media screen and (max-width: 1366px) {
  .status-bar {
    padding: 6px 16px;

    .status-wrapper {
      margin-left: 16px;
      gap: 12px;

      .status-scroll {
        flex-basis: 360px;
        max-width: 360px;
      }

      .status-scroll-btn {
        display: block;
      }
    }

    .station-name {
      max-width: 200px;
      font-size: 14px;
    }
  }
}

@media screen and (max-width: 1200px) {
  .status-bar {
    .status-wrapper {
      margin-left: 0;
      gap: 12px;
      justify-content: flex-end;
    }
  }
}
</style>
